import React, { useState } from 'react';
import { toast } from 'react-toastify';
import FontAwesomeIcon from 'src/components/Icon';
import Callout from 'src/components/InformationCallout/Callout';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import {
  ICalcomEventType,
  IPropertyBookingLink,
  IUpdatePropertyBookingLinkRequest,
  JuniperEventType,
} from '../../types';
import BookingLinkForm, { BookingLinkFormValues } from './BookingLinkForm';

interface BookingLinkEditableRowProps {
  link: IPropertyBookingLink;
  eventTypes: ICalcomEventType[];
  kindLabel: string;
  isSubmitting: boolean;
  onUpdate: (
    bookingLinkId: string,
    request: IUpdatePropertyBookingLinkRequest
  ) => Promise<boolean>;
  onRemove: (bookingLinkId: string) => Promise<boolean>;
}

const describeEventType = (eventType: ICalcomEventType): string => {
  return `${eventType.title} (${eventType.lengthInMinutes} min)`;
};

const describeDownstreamKind = (kind: JuniperEventType): string => {
  if (kind === JuniperEventType.Walkthrough) {
    return 'walkthroughs';
  }

  if (kind === JuniperEventType.KeyPickup) {
    return 'key pickups';
  }

  return 'service requests';
};

const BookingLinkEditableRow = ({
  link,
  eventTypes,
  kindLabel,
  isSubmitting,
  onUpdate,
  onRemove,
}: BookingLinkEditableRowProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState<boolean>(false);

  const requestRemove = (): void => {
    setIsConfirmingRemove(true);
  };

  const cancelRemove = (): void => {
    setIsConfirmingRemove(false);
  };

  const confirmRemove = async (): Promise<void> => {
    const succeeded: boolean = await onRemove(link.id);

    if (succeeded) {
      setIsConfirmingRemove(false);
      toast.success('Booking link removed');
    }
  };

  const selectedEventType: ICalcomEventType | undefined = eventTypes.find(
    (eventType: ICalcomEventType) => eventType.id === link.calcomEventTypeId
  );

  const enterEdit = (): void => {
    setIsEditing(true);
  };

  const cancelEdit = (): void => {
    setIsEditing(false);
  };

  const saveEdit = async (values: BookingLinkFormValues): Promise<void> => {
    const succeeded: boolean = await onUpdate(link.id, {
      label: values.label,
      calcomEventTypeId: values.calcomEventTypeId,
    });

    if (succeeded) {
      setIsEditing(false);
      toast.success('Booking link updated');
    }
  };

  if (!isEditing) {
    return (
      <div className="booking-link-editable-row">
        <div className="row-summary">
          <span className="row-label">{link.label}</span>
          {selectedEventType ? (
            <span className="row-event-type">
              {describeEventType(selectedEventType)}
            </span>
          ) : (
            <Callout
              variant="error"
              icon={
                <FontAwesomeIcon glyph="exclamation-triangle" weight="solid" />
              }
              title="Event type missing"
              subtitle="It was likely deleted in Cal.com. An event type can't be changed on an existing link, so delete this link and create a new one pointing at a valid event type."
            />
          )}

          {link.url && (
            <span className="row-booking-url">{link.url}</span>
          )}
        </div>

        <div className="row-actions">
          {selectedEventType && (
            <button
              type="button"
              className="row-icon-button"
              onClick={enterEdit}
              aria-label={`Edit ${link.label}`}
            >
              <FontAwesomeIcon glyph="edit" weight="solid" />
            </button>
          )}
          <button
            type="button"
            className="row-icon-button row-icon-button--danger"
            onClick={() => requestRemove()}
            disabled={isSubmitting}
            aria-label={`Remove ${link.label}`}
          >
            <FontAwesomeIcon glyph="trash" weight="solid" />
          </button>
        </div>

        {isConfirmingRemove && (
          <ConfirmationModal
            variant="danger"
            title="Delete this booking link?"
            confirmLabel="Delete link"
            requireTypeToConfirm
            confirmationWord="I UNDERSTAND DELETE"
            isSubmitting={isSubmitting}
            onCancel={() => cancelRemove()}
            onConfirm={() => confirmRemove()}
            message={
              <>
                <strong>{link.label}</strong> will be permanently deleted, and
                no new {describeDownstreamKind(link.kind)} can be scheduled
                against it.
              </>
            }
            warningMessage={
              <>
                Any {describeDownstreamKind(link.kind)} already invited or
                booked stay intact — this only removes the link from Juniper, so
                its scheduling link stops taking new bookings. This can&apos;t
                be undone.
              </>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="booking-link-editable-row booking-link-editable-row--editing">
      <BookingLinkForm
        eventTypes={eventTypes}
        kindLabel={kindLabel}
        initialLabel={link.label}
        initialEventTypeId={link.calcomEventTypeId}
        submitLabel="Save"
        isSubmitting={isSubmitting}
        onCancel={cancelEdit}
        onSubmit={saveEdit}
        isEditing={true}
      />
    </div>
  );
};

export default BookingLinkEditableRow;
